import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useAppState } from "Context/StateContext";
import { Member } from "Models/members/member";
import { Filters, Searches } from "Models/shipeezi.table";
import { TaskType } from "Models/task.type";
import { useTranslation } from "react-i18next";
import TasksTypesService from "Services/task.type.service";
import TimesheetService from "Services/timesheet.service";
import { CACHE_TIME, STALE_TIME } from "Util/constants";
import { Status } from "Util/enums";
import getRelatedSearch from "Util/getRelatedSearch";
import { handlesGrid } from "Util/handlesGrid";
import { TaskTypeKeys, TimesheetKeys } from "Util/queries";

import { useTimesheet } from "../../../../hooks/useTimesheet";
import { TimesheetHeader } from "../../../Tabs/Timesheet/components/Header";

interface ITimeSheetSection {
  member: Member;
}

export const TimeSheetSection = (props: ITimeSheetSection) => {
  const { member } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });
  const { timesheetGridColumns } = useTimesheet();

  const { data: taskTypes = [] } = useQuery({
    queryKey: TaskTypeKeys.getAll([]),
    queryFn: async () => {
      const response = await TasksTypesService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response?.data?.length) return response.data as TaskType[];

      return [];
    },
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { data: timesheetList, isFetching } = useQuery({
    queryKey: TimesheetKeys.getAll([page, pageSize, filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.getAll({
        page,
        pageSize,
        memberId: member?.id,
        include: JSON.stringify([
          {
            association: "member",
            reference: ["id", "fullName", "name", "lastName"],
          },
          {
            association: "task",
            include: [
              {
                association: "job",
                reference: ["id", "reference"],
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference"),
              },
              {
                association: "team",
                attributes: ["id", "name"],
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name"),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") || getRelatedSearch(searches, "teamName")
            ),
          },
        ]),
        ...filters,
      });

      if (response?.data?.length && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
  });

  const { data: timesheetCount } = useQuery({
    queryKey: TimesheetKeys.count([filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.count({
        memberId: member?.id,
        include: JSON.stringify([
          {
            association: "member",
          },
          {
            association: "task",
            include: [
              {
                association: "job",
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference"),
              },
              {
                association: "team",
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name"),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") || getRelatedSearch(searches, "teamName")
            ),
          },
        ]),
        ...filters,
      });

      if (response?.data !== undefined || response?.data !== null) return response.data;

      return 0;
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
  });

  const handleSearch = (searches: Searches) => {
    setSearches(searches);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "taskType",
          name: translate("task"),
          options:
            taskTypes && taskTypes?.length > 0
              ? taskTypes?.map((taskType) => {
                  return taskType.name;
                })
              : [],
          optionsSelected: filters?.taskType || [],
        },
      ],
      onChange: handleFilter,
    },
  };

  const { data: timesheetWidgets } = useQuery({
    queryKey: TimesheetKeys.getWidgets([filters, searches]),
    queryFn: async () => {
      const response = await TimesheetService.getWidgets({
        memberId: member?.id,
        include: JSON.stringify([
          {
            attributes: [],
            association: "task",
            where: filters || {},
            include: [
              {
                attributes: [],
                association: "job",
                required: !!getRelatedSearch(searches, "reference"),
                where: getRelatedSearch(searches, "reference", undefined, true),
              },
              {
                attributes: [],
                association: "team",
                required: !!getRelatedSearch(searches, "teamName"),
                where: getRelatedSearch(searches, "teamName", "name", true),
              },
            ],
            required: !!(
              getRelatedSearch(searches, "reference") ||
              getRelatedSearch(searches, "teamName") ||
              filters?.type?.length
            ),
          },
        ]),
      });

      if (response?.data !== undefined || response?.data !== null) return response.data;

      return {
        workHours: null,
        breakHours: null,
        totalTimeHours: null,
        mostCommonTask: null,
      };
    },
    enabled: !!member?.id,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  return (
    <>
      <TimesheetHeader timesheetWidgets={timesheetWidgets} />
      <ShipeeziNewGrid
        loadingData={isFetching}
        data={timesheetList || []}
        totalItems={timesheetCount || 0}
        columns={timesheetGridColumns}
        showSelection={false}
        showSort={false}
        pageSize={pageSize}
        pageSizeList={[10, 20, 40, 60]}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        showFooterGrid
        positionActionsColumn="right"
        generalActions={generalActions}
        keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
        emptyData={translate("noInformationToBeShown")}
      />
    </>
  );
};
